import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { useChantiersRealtime } from "@/hooks/useChantiersRealtime";
import KPICard from "@/components/KPICard";
import RealtimeProjectCard from "@/components/RealtimeProjectCard";
import EmptyState from "@/components/EmptyState";
import ChecklistPanel from "@/components/dashboard/ChecklistPanel/index";
import { CelebrationModal } from "@/components/dashboard/CelebrationModal";
import { WelcomeModal } from "@/components/dashboard/WelcomeModal";
import { MiroAnnotations } from "@/components/annotations/MiroAnnotations";
import { useChecklistStore } from "@/stores/checklistStore";
import { useChantierFilterStore } from "@/stores/chantierFilterStore";
import { useOnboardingChecklist } from "@/hooks/useOnboardingChecklist";
import { TooltipTrigger } from "@/components/tooltips/TooltipSystem";
import { TrendingUp, Users, AlertTriangle, Building, Plus, Brain, Filter, CheckCircle2 } from "lucide-react";
import { canBeArchived, STATUTS_CONFIG, type StatutChantier } from "@/constants/statuts";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { ChantierFormDialog } from "@/components/chantiers/ChantierFormDialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/lib/toast-utils";
import { labels, emptyStates, tooltips, toasts } from "@/lib/content";
import PlanningPanel from "@/components/dashboard/PlanningPanel/index";
import PilotagePanel from "@/components/dashboard/PilotagePanel/index";
import { SEUILS_MARGE } from "@/lib/calculs-rentabilite";
import { format } from "date-fns";
import { getDateFinEffective, AffectationRetard, SuspensionInfo } from "@/lib/dateUtils";
import { ModalSuspensionChantier } from "@/components/project/ModalSuspensionChantier";
import { ModalRepriseChantier } from "@/components/project/ModalRepriseChantier";
import { ModalDemarrageAuto } from "@/components/chantiers/ModalDemarrageAuto";
import { ModalDemarrageBrouillon } from "@/components/chantiers/ModalDemarrageBrouillon";
import { useAutoStartChantiers } from "@/hooks/useAutoStartChantiers";
import { useChantierArchive } from "@/hooks/useChantierArchive";
import { useChantierStatus } from "@/hooks/useChantierStatus";

import type { SuspensionInfo } from "@/types/calendar";

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  // toast Sonner importé depuis @/lib/toast-utils
  const [stats, setStats] = useState({
    totalProjects: 0,
    avgRentabilite: 0,
    totalTeam: 0,
    membresSurChantier: 0,
    alertsCount: 0,
    completedSuccessfully: 0, // Chantiers clôturés avec succès (via ChantierClosureDialog)
  });
  // État pour savoir si au moins un chantier a une analyse IA disponible (cooldown terminé)
  const [hasAnalyseDisponible, setHasAnalyseDisponible] = useState(false);
  const [recentProjects, setRecentProjects] = useState<any[]>([]);
  const [allProjects, setAllProjects] = useState<any[]>([]);
  // Filtre local au Dashboard (découplé du store partagé Projects/Rentabilité)
  const [filterEtat, setFilterEtat] = useState('all');
  const [entrepriseId, setEntrepriseId] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // États pour les modals de suspension et reprise
  const [suspensionModalOpen, setSuspensionModalOpen] = useState(false);
  const [repriseModalOpen, setRepriseModalOpen] = useState(false);
  const [selectedChantierForSuspension, setSelectedChantierForSuspension] = useState<any | null>(null);
  const [suspensionInfoForReprise, setSuspensionInfoForReprise] = useState<SuspensionInfo | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  // État pour le modal de démarrage de brouillon
  const [brouillonADemarrer, setBrouillonADemarrer] = useState<any | null>(null);

  // Zustand store pour ouvrir la checklist
  const { openPanel } = useChecklistStore();

  // État pour le modal de démarrage automatique - chantier sélectionné pour suspension
  const [autoStartSuspendChantier, setAutoStartSuspendChantier] = useState<string | null>(null);

  // Définition de loadDashboardData AVANT les hooks qui l'utilisent
  const loadDashboardData = useCallback(async () => {
    try {
      // Get entreprise ID
      const { data: entrepriseData } = await supabase
        .from("entreprises")
        .select("id")
        .eq("proprietaire_user_id", user?.id)
        .single();

      if (!entrepriseData) return;
      setEntrepriseId(entrepriseData.id);

      // Get projects with metrics from chantier_metrics_realtime (inclure aussi les archivés)
      const { data: projects } = await supabase
        .from("chantiers")
        .select(`
          *,
          chantier_metrics_realtime (metrics)
        `)
        .eq("entreprise_id", entrepriseData.id)
        .order("date_creation", { ascending: false });

      // Get team members (tous les membres actifs, pas en_arret)
      const { data: team } = await supabase
        .from("membres_equipe")
        .select("id, statut")
        .eq("entreprise_id", entrepriseData.id)
        .neq("statut", "en_arret");

      // Récupérer les affectations actives aujourd'hui pour calculer le statut réel
      const todayStr = new Date().toISOString().split('T')[0];
      const { data: affectationsActives } = await supabase
        .from("affectations_chantiers")
        .select(`
          membre_equipe_id,
          chantiers!inner(entreprise_id, etat_chantier)
        `)
        .eq("chantiers.entreprise_id", entrepriseData.id)
        .in("chantiers.etat_chantier", ['en_cours', 'planifie'])
        .lte("date_debut", todayStr)
        .or(`date_fin.is.null,date_fin.gte.${todayStr}`);

      // Set des membres avec affectation active aujourd'hui
      const membresAvecAffectation = new Set(
        affectationsActives?.map(a => a.membre_equipe_id) || []
      );

      const totalTeam = team?.length || 0;
      const membresSurChantier = team?.filter(m => membresAvecAffectation.has(m.id)).length || 0;

      // Mapper tous les projets avec leurs métriques + vérifier analyses IA + retards
      const projectsWithMetrics = await Promise.all(
        (projects || []).map(async (project) => {
          // Vérifier si une analyse IA existe
          let hasAiAnalysis = false;

          // Essayer la nouvelle table analyses_ia
          const { count: newCount, error: newErr } = await supabase
            .from("analyses_ia" as any)
            .select("id", { count: 'exact', head: true })
            .eq("chantier_id", project.id);
          if (!newErr && (newCount || 0) > 0) {
            hasAiAnalysis = true;
          }

          // Si pas trouvé, essayer l'ancienne table ai_analyses
          if (!hasAiAnalysis) {
            const { count: oldCount, error: oldErr } = await supabase
              .from("ai_analyses")
              .select("id", { count: 'exact', head: true })
              .eq("chantier_id", project.id);
            if (!oldErr && (oldCount || 0) > 0) {
              hasAiAnalysis = true;
            }
          }

          // Récupérer les affectations avec jours_travailles, jours_details et heures_par_jour
          const { data: allAffectations } = await supabase
            .from("affectations_chantiers")
            .select("date_debut, date_fin, notes, jours_travailles, jours_details, heures_par_jour")
            .eq("chantier_id", project.id)
            .order("date_debut", { ascending: true });

          // Filtrer les affectations de retard spécifiquement
          const delayAffectations = allAffectations?.filter(a => a.notes?.startsWith("Retard chantier")) || [];

          // Extraire heures_par_jour de la première affectation "normale" (pas retard)
          const affectationsNormales = allAffectations?.filter(a => !a.notes?.startsWith("Retard chantier")) || [];
          const heuresParJour = affectationsNormales[0]?.heures_par_jour || 8;

          // Agréger tous les jours_details de toutes les affectations
          let allJoursDetails = allAffectations?.flatMap(a => a.jours_details || []) || [];

          // Si jours_details est vide, le GÉNÉRER depuis les dates des affectations
          if (allJoursDetails.length === 0 && allAffectations && allAffectations.length > 0) {
            allJoursDetails = [];

            for (const aff of allAffectations) {
              if (!aff.date_debut || !aff.date_fin) continue;

              const start = new Date(aff.date_debut);
              const end = new Date(aff.date_fin);
              let current = new Date(start);

              while (current <= end) {
                // Ignorer les weekends (0 = dimanche, 6 = samedi)
                if (current.getDay() !== 0 && current.getDay() !== 6) {
                  allJoursDetails.push({
                    date: current.toISOString().split('T')[0],
                    period: 'FULL' as const
                  });
                }
                current.setDate(current.getDate() + 1);
              }
            }
          }

          // Préparer les données retard pour getDateFinEffective
          const affectationsRetard: AffectationRetard[] = delayAffectations.map(a => ({
            date_fin: a.date_fin,
            jours_travailles: a.jours_travailles || 0,
            jours_details: a.jours_details as AffectationRetard['jours_details']
          }));

          // Charger les suspensions terminées (avec date_reprise)
          const { data: suspensionsTerminees } = await supabase
            .from("suspensions_chantiers")
            .select("date_suspension, date_reprise, duree_suspension_jours")
            .eq("chantier_id", project.id)
            .not("date_reprise", "is", null);

          const suspensionsData: SuspensionInfo[] = (suspensionsTerminees || []).map(s => ({
            date_suspension: s.date_suspension,
            date_reprise: s.date_reprise,
            duree_suspension_jours: s.duree_suspension_jours
          }));

          // Calculer date_fin_effective avec la source unique de vérité
          const dateFinEffectiveDate = getDateFinEffective({
            date_fin_estimee: project.date_fin_estimee,
            date_fin_initiale: project.date_fin_initiale,
            affectationsRetard,
            suspensionsTerminees: suspensionsData
          });
          const dateFinEffective = dateFinEffectiveDate
            ? format(dateFinEffectiveDate, 'yyyy-MM-dd')
            : project.date_fin_estimee;

          // Calculer les dates de retard (min date_debut, max date_fin)
          let dateDebutRetard: string | undefined;
          let dateFinRetard: string | undefined;
          let joursRetard = 0;
          if (delayAffectations.length > 0) {
            // Trier par date_debut pour avoir la première
            const sortedByDebut = [...delayAffectations].sort((a, b) => a.date_debut.localeCompare(b.date_debut));
            dateDebutRetard = sortedByDebut[0].date_debut;
            dateFinRetard = delayAffectations.reduce((max, a) =>
              a.date_fin > max ? a.date_fin : max, delayAffectations[0].date_fin);
            // Calculer le total des jours de retard
            joursRetard = delayAffectations.reduce((sum, a) => sum + (a.jours_travailles || 0), 0);
          }

          return {
            ...project,
            // Relation chantier_metrics_realtime est 1-1, pas un tableau
            metrics: (project as any).chantier_metrics_realtime?.metrics as any,
            hasAiAnalysis,
            hasDelayRegistered: delayAffectations.length > 0,
            dateDebutRetard,
            dateFinRetard,
            joursRetard,      // Nombre de jours ouvrés de retard (pour calcul progression)
            dateFinEffective, // Nouvelle propriété : date_fin la plus tardive
            heuresParJour,    // Heures par jour depuis première affectation (pour calcul progression)
            allJoursDetails,  // Tous les jours_details agrégés (pour calcul progression)
          };
        })
      );

      // Filtrer seulement les actifs pour les stats (non archivés)
      // Utilise etat_chantier au lieu de statut pour cohérence avec le reste de l'app
      const activeProjects = projectsWithMetrics.filter(p => p.etat_chantier !== 'archive');
      const totalProjects = activeProjects.length;

      // Average rentabilite from metrics (seulement projets actifs)
      const projectsWithRentabilite = activeProjects.filter(p =>
        p.metrics?.marge_finale_pct != null
      );
      
      const avgRentabilite = projectsWithRentabilite.length > 0
        ? projectsWithRentabilite.reduce((sum, p) => sum + (p.metrics?.marge_finale_pct || 0), 0) / projectsWithRentabilite.length
        : 0;

      // Count alerts (marge_finale < 10%, seulement projets actifs)
      const alertsCount = activeProjects.filter(p => (p.metrics?.marge_finale_pct || 0) < SEUILS_MARGE.BON).length;

      // Compter les chantiers clôturés avec succès (termine OU archive avec cloture_avec_succes = true)
      // Inclut les archivés pour conserver l'historique des succès
      const completedSuccessfully = projectsWithMetrics.filter(p =>
        p.cloture_avec_succes === true
      ).length;

      setStats({
        totalProjects,
        avgRentabilite,
        totalTeam,
        membresSurChantier,
        alertsCount,
        completedSuccessfully,
      });

      // Vérifier si au moins un chantier actif a une analyse IA disponible (cooldown terminé)
      // On vérifie tous les chantiers actifs en parallèle
      if (activeProjects.length > 0) {
        const cooldownChecks = await Promise.all(
          activeProjects.map(async (project) => {
            const { data } = await supabase.rpc('peut_analyser_chantier', {
              p_chantier_id: project.id
            });
            return data?.peut_analyser === true;
          })
        );
        // Au moins un chantier peut être analysé ?
        const anyAvailable = cooldownChecks.some(canAnalyze => canAnalyze);
        setHasAnalyseDisponible(anyAvailable);
      } else {
        setHasAnalyseDisponible(false);
      }

      // Stocker tous les projets pour le filtrage
      setAllProjects(projectsWithMetrics);
    } catch (error) {
      console.error("Erreur chargement dashboard:", error);
    }
  }, [user]);

  // Charger les données au montage et quand l'utilisateur change
  // + Polling toutes les 5 minutes pour les métriques temps réel (coûts horaires)
  useEffect(() => {
    if (!user) return;

    // Chargement initial
    loadDashboardData();

    // Polling toutes les 5 minutes pour actualiser les métriques temps réel
    // (les coûts horaires sont calculés côté SQL et ne déclenchent pas le realtime)
    const POLLING_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes
    const intervalId = setInterval(loadDashboardData, POLLING_INTERVAL_MS);

    return () => {
      clearInterval(intervalId);
    };
  }, [user, loadDashboardData]);

  // Écouter l'événement analyse-ia-complete pour masquer la bannière après une analyse
  useEffect(() => {
    const handleAnalyseComplete = () => {
      // Recharger les données pour vérifier si d'autres chantiers ont une analyse disponible
      loadDashboardData();
    };

    window.addEventListener('analyse-ia-complete', handleAnalyseComplete);
    return () => {
      window.removeEventListener('analyse-ia-complete', handleAnalyseComplete);
    };
  }, [loadDashboardData]);

  // Options du filtre Dashboard (dynamiques, masque les filtres à 0 résultats)
  const dashboardFilterOptions = useMemo(() => {
    const nonArchived = allProjects.filter(p => p.etat_chantier !== 'archive');
    const options = [
      { value: "all", label: "Tous", count: nonArchived.length, alwaysShow: true },
      { value: "en_cours", label: "🏗️ En cours", count: nonArchived.filter(p => p.etat_chantier === 'en_cours').length, alwaysShow: false },
      { value: "termine", label: "✅ Terminé", count: allProjects.filter(p => p.etat_chantier === 'termine').length, alwaysShow: false },
      { value: "avec_depenses", label: "💰 Avec dépenses", count: nonArchived.filter(p => {
        const couts = (p.metrics?.couts_fixes_engages || 0) + (p.metrics?.cout_main_oeuvre_reel || 0);
        return couts > 0;
      }).length, alwaysShow: false },
    ];
    return options.filter(o => o.alwaysShow || o.count > 0);
  }, [allProjects]);

  // Filtrer les projets selon l'état sélectionné
  // "Tous" = non-archivés par défaut
  useEffect(() => {
    const filtered = allProjects.filter(project => {
      // "all" = tous sauf archivés
      if (filterEtat === "all") return project.etat_chantier !== 'archive';
      // "avec_depenses" = projets non-archivés avec coûts réels > 0
      if (filterEtat === "avec_depenses") {
        const couts = (project.metrics?.couts_fixes_engages || 0) + (project.metrics?.cout_main_oeuvre_reel || 0);
        return couts > 0 && project.etat_chantier !== 'archive';
      }
      // Sinon filtrer par etat_chantier
      return project.etat_chantier === filterEtat;
    }).slice(0, 9);
    setRecentProjects(filtered);
  }, [filterEtat, allProjects]);

  // Setup Realtime subscriptions
  const handleRealtimeChange = useCallback(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useChantiersRealtime(entrepriseId, handleRealtimeChange);

  // Hook pour détecter les chantiers planifiés dont la date de début est atteinte
  const {
    chantierCourant: autoStartChantier,
    isModalOpen: autoStartModalOpen,
    totalChantiers: autoStartTotal,
    currentIndex: autoStartIndex,
    handleAction: handleAutoStartAction,
    handleNext: handleAutoStartNext,
    isProcessing: autoStartProcessing,
  } = useAutoStartChantiers(entrepriseId, loadDashboardData);

  // Hook centralisé pour l'archivage/désarchivage
  const { archiver, desarchiver } = useChantierArchive({
    onSuccess: loadDashboardData
  });

  const { changeStatus } = useChantierStatus({
    onSuccess: loadDashboardData
  });

  // Handler pour l'option "Suspendre" du modal de démarrage automatique
  const handleAutoStartSuspendre = useCallback((chantierId: string) => {
    setAutoStartSuspendChantier(chantierId);
    // On ferme le modal de démarrage auto, on récupère les infos du chantier
    const project = allProjects.find(p => p.id === chantierId);
    if (project) {
      setSelectedChantierForSuspension(project);
      setSuspensionModalOpen(true);
    }
    handleAutoStartNext();
  }, [allProjects, handleAutoStartNext]);

  // Hook onboarding checklist pour détecter la complétion
  const { isAllCompleted } = useOnboardingChecklist(entrepriseId || undefined);

  // Célébration quand l'onboarding est complété pour la première fois
  useEffect(() => {
    const CELEBRATION_KEY = 'b8ild_onboarding_celebrated';
    const alreadyCelebrated = localStorage.getItem(CELEBRATION_KEY);

    if (isAllCompleted && !alreadyCelebrated && entrepriseId) {
      // Petit délai pour laisser le temps au dashboard de se charger
      const timer = setTimeout(() => {
        setShowCelebration(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isAllCompleted, entrepriseId]);

  // Callback pour fermer la célébration et marquer comme vue
  const handleCloseCelebration = () => {
    localStorage.setItem('b8ild_onboarding_celebrated', 'true');
    setShowCelebration(false);
  };

  // Bienvenue modal - affichée une seule fois après premier accès post-paiement
  useEffect(() => {
    const WELCOME_KEY = 'b8ild_welcome_shown';
    const alreadyShown = localStorage.getItem(WELCOME_KEY);

    if (!alreadyShown && entrepriseId) {
      // Petit délai pour laisser le temps au dashboard de se charger
      const timer = setTimeout(() => {
        setShowWelcome(true);
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [entrepriseId]);

  // Callback pour fermer welcome et commencer le guide
  const handleWelcomeStartGuide = () => {
    localStorage.setItem('b8ild_welcome_shown', 'true');
    setShowWelcome(false);
    // Ouvrir la checklist "Premiers pas"
    openPanel();
  };

  // Callback pour fermer welcome et explorer librement
  const handleWelcomeExplore = () => {
    localStorage.setItem('b8ild_welcome_shown', 'true');
    setShowWelcome(false);
  };

  const handleDeleteProject = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce chantier définitivement ?")) {
      return;
    }

    try {
      // Supprimer le chantier (CASCADE supprimera automatiquement toutes les dépendances)
      const { error } = await supabase
        .from("chantiers")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast.success("Chantier supprimé", { description: "Le chantier a été supprimé définitivement." });
      
      loadDashboardData();
    } catch (error: any) {
      console.error("Erreur suppression chantier:", error);
      toast.error("Erreur", { description: error.message || "Impossible de supprimer le chantier" });
    }
  };

  // Handler pour changer l'état directement depuis le menu
  // Utilise le hook centralisé useChantierStatus pour validation et synchronisation
  const handleChangeState = async (id: string, newState: string) => {
    const project = allProjects.find(p => p.id === id);
    if (!project) return;

    const currentStatus = (project.etat_chantier || 'en_cours') as StatutChantier;
    const targetStatus = newState as StatutChantier;

    // Utiliser le hook centralisé pour la transition
    const result = await changeStatus(id, currentStatus, targetStatus);

    // Si un modal est requis, l'ouvrir
    if (!result.success && result.modalRequired) {
      switch (result.modalRequired) {
        case 'ChantierClosureDialog':
          navigate(`/projects/${id}?terminer=true`);
          break;

        case 'ModalSuspensionChantier':
          setSelectedChantierForSuspension(project);
          setSuspensionModalOpen(true);
          break;

        case 'ModalRepriseChantier':
          // Charger la suspension active du chantier
          try {
            const { data: suspension } = await supabase
              .from('suspensions_chantiers')
              .select('*')
              .eq('chantier_id', id)
              .is('date_reprise', null)
              .order('date_suspension', { ascending: false })
              .limit(1)
              .single();

            if (suspension) {
              setSelectedChantierForSuspension(project);
              setSuspensionInfoForReprise(suspension as SuspensionInfo);
              setRepriseModalOpen(true);
            } else {
              // Si pas de suspension trouvée, remettre directement en cours via le hook
              const directResult = await changeStatus(id, currentStatus, 'en_cours' as StatutChantier);
              if (directResult.success) {
                toast.success("Chantier repris");
              }
            }
          } catch (error) {
            console.error('[Dashboard] Erreur chargement suspension:', error);
          }
          break;

        case 'ModalAnnulationChantier':
          navigate(`/projects/${id}?annuler=true`);
          break;

        default:
          // Pour les modals non gérés ici, rediriger vers la page détail
          navigate(`/projects/${id}`);
      }
    }
    // Si success: true, le hook a déjà émis les événements et appelé onSuccess (loadDashboardData)
  };

  // Handler pour prolonger le retard (navigue vers Penalty Simulator en mode ajout)
  const handleProlongerRetard = (id: string) => {
    navigate(`/financial?tab=retard`, { state: { chantierId: id } });
  };

  // Handler pour archiver un chantier (utilise le hook centralisé)
  const handleArchive = async (id: string) => {
    const project = allProjects.find(p => p.id === id);
    if (!project) return;
    await archiver(project);
  };

  // Handler pour désarchiver un chantier (restaure le statut d'avant archivage)
  const handleUnarchive = async (id: string) => {
    const project = allProjects.find(p => p.id === id);
    if (!project) return;
    await desarchiver(project);
  };

  // Handler pour démarrer un brouillon (ouvre le modal de confirmation avec vérification des conflits)
  const handleDemarrerBrouillon = (id: string) => {
    const project = allProjects.find(p => p.id === id);
    if (!project) return;

    // Ouvrir le modal de confirmation des dates avec vérification des conflits
    setBrouillonADemarrer({
      id: project.id,
      nom: project.nom_chantier,
      date_debut_prevue: project.date_debut_prevue,
      date_fin_estimee: project.date_fin_estimee,
      client_nom: project.client,
      entreprise_id: entrepriseId,
    });
  };

  return (
    <>
      {/* Contenu principal avec animation - NE PAS inclure les panneaux fixed ici
          car le transform de l'animation casse le positionnement fixed des enfants */}
      <div className="space-y-8 animate-fade-up">
        <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-black text-gradient-primary">{labels.nav.dashboard}</h1>
          <p className="text-muted-foreground mt-2 text-lg">
            Bienvenue {user?.user_metadata?.prenom} ! Voici un aperçu de votre activité.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
        <TooltipTrigger tooltipId="dashboard_overview" showOnMount delay={500}>
          <KPICard
            title="Chantiers actifs"
            value={stats.totalProjects}
            icon={Building}
            subtitle="chantiers en cours"
            tooltip="Nombre total de chantiers actuellement en cours (hors archivés et terminés)."
            href="/projects?status=en_cours"
          />
        </TooltipTrigger>
        <KPICard
          title="Rentabilité moyenne"
          value={`${stats.avgRentabilite.toFixed(1)}%`}
          icon={TrendingUp}
          subtitle="sur chantiers en cours"
          tooltip="Moyenne de la marge finale estimée sur l'ensemble de vos chantiers actifs. Une rentabilité supérieure à 15% est généralement considérée comme bonne."
          href="/rentabilite"
        />
        <KPICard
          title="Membres équipe"
          value={`${stats.membresSurChantier}/${stats.totalTeam}`}
          icon={Users}
          subtitle="sur chantier / total"
          tooltip="Nombre de membres actuellement affectés sur un chantier par rapport à l'effectif total disponible dans votre entreprise."
          href="/team?status=sur_chantier"
        />
        <KPICard
          title="Alertes en cours"
          value={stats.alertsCount}
          icon={AlertTriangle}
          subtitle="chantiers à surveiller"
          tooltip="Nombre de chantiers dont la marge finale estimée est inférieure à 18% (seuil BTP). Ces chantiers nécessitent une attention particulière."
          href="/projects?alerte=marge_critique"
        />
        <KPICard
          title="Terminés"
          value={stats.completedSuccessfully}
          icon={CheckCircle2}
          subtitle="clôturés avec succès"
          tooltip="Nombre de chantiers correctement clôturés via le processus de clôture (inclut les archivés)."
          href="/projects?status=termine"
        />
      </div>

      {/* Badge IA compact — masqué pour simplifier le dashboard */}
      {false && hasAnalyseDisponible && (
        <div className="flex items-center">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-purple-500/10 text-purple-400 border border-purple-500/20">
            <Brain className="h-3 w-3" />
            Analyse IA disponible — Accédez à l'onglet "Analyse IA" depuis un chantier
          </span>
        </div>
      )}

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-black">Chantiers récents</h2>
          <div className="flex gap-2">
            <TooltipTrigger tooltipId="create_chantier" showOnMount delay={1000}>
              <Button size="sm" className="gap-1.5 font-semibold" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4" />
                Nouveau chantier
              </Button>
            </TooltipTrigger>
            <ChantierFormDialog
              open={dialogOpen}
              onOpenChange={setDialogOpen}
              project={null}
              onSuccess={loadDashboardData}
              entrepriseId={entrepriseId}
            />
            <Select value={filterEtat} onValueChange={setFilterEtat}>
              <SelectTrigger className="w-[180px] bg-transparent border-2 border-amber-500/70 hover:border-amber-400 hover:bg-amber-500/10 transition-colors">
                <Filter className="h-4 w-4 mr-2 text-amber-500" />
                <SelectValue placeholder="Tous" />
              </SelectTrigger>
              <SelectContent>
                {dashboardFilterOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {recentProjects.length === 0 ? (
          <EmptyState
            icon={Building}
            title={emptyStates.dashboard.title}
            text={emptyStates.dashboard.text}
            primaryAction={{
              label: emptyStates.dashboard.primary,
              onClick: () => navigate("/projects"),
            }}
            secondaryAction={{
              label: emptyStates.dashboard.secondary,
              onClick: () => navigate("/team"),
            }}
          />
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {recentProjects.map((project) => (
              <RealtimeProjectCard
                key={project.id}
                project={project}
                hasAiAnalysis={project.hasAiAnalysis}
                hasDelayRegistered={project.hasDelayRegistered}
                dateDebutRetard={project.dateDebutRetard}
                dateFinRetard={project.dateFinRetard}
                joursRetard={project.joursRetard}
                date_fin_initiale={project.date_fin_initiale}
                dateFinEffective={project.dateFinEffective}
                heuresParJour={project.heuresParJour}
                joursDetails={project.allJoursDetails}
                onEdit={(id) => navigate(`/projects/${id}`)}
                onDelete={handleDeleteProject}
                onChangeState={handleChangeState}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
                onDemarrerBrouillon={handleDemarrerBrouillon}
                onProlongerRetard={handleProlongerRetard}
              />
            ))}
          </div>
        )}
      </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════════
          PANNEAUX LATÉRAUX FIXED - Doivent être EN DEHORS du conteneur animé
          car le transform de animate-fade-up crée un nouveau contexte de positionnement
          qui casse le position:fixed des panneaux
          ═══════════════════════════════════════════════════════════════════════ */}

      {/* Panneau Centre de Pilotage (au-dessus du Planning) */}
      {entrepriseId && (
        <TooltipTrigger tooltipId="pilotage_panel">
          <PilotagePanel entrepriseId={entrepriseId} />
        </TooltipTrigger>
      )}

      {/* Panneau Planning latéral */}
      {entrepriseId && (
        <TooltipTrigger tooltipId="planning_calendar">
          <PlanningPanel entrepriseId={entrepriseId} />
        </TooltipTrigger>
      )}

      {/* Panneau Checklist "Premiers pas" - Onglet dépliable */}
      {entrepriseId && (
        <ChecklistPanel entrepriseId={entrepriseId} />
      )}

      {/* Modal Suspension */}
      {selectedChantierForSuspension && (
        <ModalSuspensionChantier
          isOpen={suspensionModalOpen}
          onClose={() => {
            setSuspensionModalOpen(false);
            setSelectedChantierForSuspension(null);
          }}
          chantierId={selectedChantierForSuspension.id}
          chantierNom={selectedChantierForSuspension.nom_chantier}
          onConfirm={() => {
            setSuspensionModalOpen(false);
            setSelectedChantierForSuspension(null);
            loadDashboardData();
          }}
        />
      )}

      {/* Modal Reprise */}
      {selectedChantierForSuspension && suspensionInfoForReprise && (
        <ModalRepriseChantier
          isOpen={repriseModalOpen}
          onClose={() => {
            setRepriseModalOpen(false);
            setSelectedChantierForSuspension(null);
            setSuspensionInfoForReprise(null);
          }}
          chantierId={selectedChantierForSuspension.id}
          chantierNom={selectedChantierForSuspension.nom_chantier}
          suspensionInfo={suspensionInfoForReprise}
          entrepriseId={entrepriseId || undefined}
          onConfirm={() => {
            setRepriseModalOpen(false);
            setSelectedChantierForSuspension(null);
            setSuspensionInfoForReprise(null);
            loadDashboardData();
          }}
        />
      )}

      {/* Modal Démarrage Automatique - Chantiers planifiés dont la date de début est atteinte */}
      <ModalDemarrageAuto
        isOpen={autoStartModalOpen}
        chantier={autoStartChantier}
        currentIndex={autoStartIndex}
        totalChantiers={autoStartTotal}
        isProcessing={autoStartProcessing}
        onAction={handleAutoStartAction}
        onSuspendre={handleAutoStartSuspendre}
      />

      {/* Modal Démarrage Brouillon - Confirmation dates + vérification conflits */}
      <ModalDemarrageBrouillon
        isOpen={!!brouillonADemarrer}
        onClose={() => setBrouillonADemarrer(null)}
        chantier={brouillonADemarrer}
        onDemarrer={() => {
          setBrouillonADemarrer(null);
          loadDashboardData();
        }}
      />

      {/* Modal Bienvenue - Première connexion après paiement */}
      {showWelcome && (
        <WelcomeModal
          prenom={user?.user_metadata?.prenom || ''}
          onStartGuide={handleWelcomeStartGuide}
          onExplore={handleWelcomeExplore}
        />
      )}

      {/* Modal Célébration Onboarding Complété */}
      {showCelebration && (
        <CelebrationModal
          prenom={user?.user_metadata?.prenom || user?.email?.split('@')[0] || 'Champion'}
          onClose={handleCloseCelebration}
        />
      )}

      {/* Annotations Miro Dashboard */}
      <MiroAnnotations
        pageId="dashboard"
        annotations={[
          {
            id: "dashboard-pilotage",
            targetSelector: "[data-miro-target='dashboard-pilotage-panel']",
            text: "Vos alertes en temps réel : retards, budgets dépassés, marges faibles",
            position: "left",
            offsetX: -30,
            offsetY: -80,  // Positionne le cadre juste en dessous du haut de la KPI card
          },
          {
            id: "dashboard-planning",
            targetSelector: "[data-miro-target='dashboard-planning-panel']",
            text: "Cliquez ici pour afficher le planning, voir les affectations et effectuer des rotations",
            position: "left",
            offsetX: -30,
            offsetY: 0,
          },
          {
            id: "dashboard-guide",
            targetSelector: "[data-miro-target='dashboard-guide-panel']",
            text: "Suivez ce guide pas à pas pour configurer votre premier chantier",
            position: "left",
            offsetX: -30,
            offsetY: 0,
          },
        ]}
      />
    </>
  );
};

export default Dashboard;
